import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  User as UserIcon, Building2, FileText, MapPin, Mail, Phone, Save,
  Loader2, Edit2, RotateCcw, AlertCircle, CheckCircle2, Wallet,
  Lock, Eye, EyeOff, KeyRound, Receipt,
} from 'lucide-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import CountryPicker from './CountryPicker';
import AvatarUploadCard from './AvatarUploadCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Components defined OUTSIDE the parent so input focus is preserved on every
// keystroke (otherwise React remounts the subtree on every state update).
const SECTION_COLOR = {
  cyan:    'from-cyan-500 to-cyan-600',
  blue:    'from-blue-500 to-blue-600',
  violet:  'from-violet-500 to-violet-600',
  amber:   'from-amber-500 to-amber-600',
  emerald: 'from-emerald-500 to-emerald-600',
};

function Section({ title, icon: Icon, color = 'cyan', children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2.5">
        <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${SECTION_COLOR[color] || SECTION_COLOR.cyan} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FieldErr({ name, errors }) {
  if (!errors[name]) return null;
  return (
    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid={`profile-error-${name}`}>
      <AlertCircle className="w-3 h-3" /> {errors[name]}
    </p>
  );
}

export default function MyProfile({ token, user, onSaved }) {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverMsg, setServerMsg] = useState({ type: '', text: '' });

  // ---- Password change (independent flow) ----
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdShow, setPwdShow] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const data = await r.json();
      setProfile(data);
      setDraft(data);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
    setServerMsg({ type: '', text: '' });
  };

  const validate = () => {
    if (!draft) return {};
    const e = {};
    if (!(draft.first_name || '').trim()) e.first_name = 'Required';
    if (!(draft.surname || '').trim())    e.surname    = 'Required';
    if (!(draft.country || '').trim())    e.country    = 'Select your country';
    if (!draft.phone || !isValidPhoneNumber(draft.phone)) e.phone = 'Valid phone with country code is required';
    if (draft.evc_registered && !(draft.evc_customer_id || '').trim()) {
      e.evc_customer_id = 'EVC customer ID is required';
    }
    if (draft.is_company) {
      if (!(draft.company_name || '').trim()) e.company_name = 'Required';
      if (!(draft.vat || '').trim())          e.vat          = 'Required';
      if (!(draft.address || '').trim())      e.address      = 'Required';
      if (!(draft.zipcode || '').trim())      e.zipcode      = 'Required';
      if (!(draft.city || '').trim())         e.city         = 'Required';
    }
    return e;
  };

  const save = async () => {
    const ev = validate();
    if (Object.keys(ev).length) {
      setErrors(ev);
      setServerMsg({ type: 'error', text: 'Please fix the highlighted fields.' });
      return;
    }
    setSaving(true); setServerMsg({ type: '', text: '' });
    try {
      const fields = [
        'first_name', 'surname', 'country', 'phone', 'phone_country',
        'evc_registered', 'evc_customer_id',
        'is_company', 'company_name', 'vat', 'address', 'zipcode', 'city',
        'subscribed_newsletter',
      ];
      const payload = {};
      fields.forEach((f) => { if (draft && f in draft) payload[f] = draft[f]; });
      const r = await fetch(`${API_URL}/api/auth/me`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setServerMsg({ type: 'error', text: body.detail || `HTTP ${r.status}` });
        return;
      }
      setProfile(body); setDraft(body); setEditing(false);
      setServerMsg({ type: 'success', text: 'Profile updated successfully.' });
      if (onSaved) onSaved();
    } catch (e) {
      setServerMsg({ type: 'error', text: e.message || 'Network error' });
    } finally { setSaving(false); }
  };

  const pwdScore = useMemo(() => {
    const p = pwdNew || '';
    let s = 0;
    if (p.length >= 8) s++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0..4
  }, [pwdNew]);

  const pwdMatch = pwdNew && pwdConfirm && pwdNew === pwdConfirm;
  const pwdValid = pwdNew.length >= 8 && pwdMatch;

  const changePassword = async () => {
    setPwdMsg({ type: '', text: '' });
    if (!pwdNew || !pwdConfirm) {
      setPwdMsg({ type: 'error', text: 'Please fill in both password fields' }); return;
    }
    if (pwdNew !== pwdConfirm) {
      setPwdMsg({ type: 'error', text: 'Passwords do not match' }); return;
    }
    if (pwdNew.length < 8) {
      setPwdMsg({ type: 'error', text: 'Password must be at least 8 characters long' }); return;
    }
    setPwdSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/me/password`, {
        method: 'POST', headers,
        body: JSON.stringify({ new_password: pwdNew, confirm_password: pwdConfirm }),
      });
      const raw = await r.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch (_) { body = { detail: raw }; }
      if (!r.ok) {
        const detail = typeof body.detail === 'string' ? body.detail : `Failed (HTTP ${r.status})`;
        setPwdMsg({ type: 'error', text: detail }); return;
      }
      setPwdMsg({ type: 'success', text: 'Password changed successfully. Use it the next time you log in.' });
      setPwdNew(''); setPwdConfirm(''); setPwdShow(false);
    } catch (e) {
      setPwdMsg({ type: 'error', text: e.message || 'Network error' });
    } finally { setPwdSaving(false); }
  };

  const errCls = (name) => errors[name]
    ? 'border-red-400 focus:ring-red-400 bg-red-50/30'
    : 'border-slate-300 focus:ring-cyan-500';

  if (loading || !profile) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-xl p-12 text-center text-slate-500 border border-slate-200">
          <Loader2 className="w-6 h-6 inline animate-spin mr-2" /> Loading profile…
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800">{t('title')}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{t('profile_subtitle', { defaultValue: 'Keep your contact and invoicing information up to date.' })}</p>
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <button
              onClick={() => { setEditing(true); setServerMsg({ type: '', text: '' }); }}
              data-testid="profile-self-edit"
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 hover:shadow-md"
            >
              <Edit2 className="w-3.5 h-3.5" /> {t('edit_profile', { defaultValue: 'Edit profile' })}
            </button>
          ) : (
            <>
              <button
                onClick={() => { setDraft(profile); setEditing(false); setErrors({}); setServerMsg({ type: '', text: '' }); }}
                disabled={saving}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-2"
                data-testid="profile-self-cancel"
              >
                <RotateCcw className="w-3.5 h-3.5" /> {t('cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={save} disabled={saving}
                data-testid="profile-self-save"
                className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 hover:shadow-md disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('save_changes', { defaultValue: 'Save changes' })}
              </button>
            </>
          )}
        </div>
      </div>

      {serverMsg.text && (
        <div className={`p-3 rounded-lg text-sm ${
          serverMsg.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`} data-testid="profile-self-msg">
          {serverMsg.type === 'success' && <CheckCircle2 className="w-4 h-4 inline mr-1.5" />}
          {serverMsg.text}
        </div>
      )}

      {/* Profile photo block (Phase C) */}
      <AvatarUploadCard
        token={token}
        avatarUrl={profile.avatar_url || null}
        name={`${profile.first_name || ''} ${profile.surname || ''}`.trim()}
        email={profile.email}
        onChange={(newUrl) => {
          setProfile(p => ({ ...p, avatar_url: newUrl }));
          setDraft(d => ({ ...d, avatar_url: newUrl }));
          // Bubble up so the parent header reflects the new photo without
          // requiring a full page reload.
          onSaved?.();
        }}
        testidPrefix="my-profile-avatar"
      />

      {/* Account stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t('stats.balance', { defaultValue: 'Balance' })}     value={`€${(profile.balance || 0).toFixed(2)}`}          icon={Wallet} color="emerald" />
        <Stat label={t('stats.reserved', { defaultValue: 'Reserved' })}   value={`€${(profile.reserved || 0).toFixed(2)}`}         icon={Wallet} color="amber" />
        <Stat label={t('stats.recharged', { defaultValue: 'Recharged' })} value={`€${(profile.recharged_amount || 0).toFixed(2)}`} icon={Wallet} color="cyan" />
        <Stat label={t('stats.member_since', { defaultValue: 'Member since' })} value={profile.registration_date ? new Date(profile.registration_date).toLocaleDateString() : '—'} icon={UserIcon} color="violet" />
      </div>

      <Section title={t('sections.personal_info', { defaultValue: 'Personal information' })} icon={UserIcon} color="cyan">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fields.first_name')} *</label>
            {editing ? (
              <input
                type="text" value={draft?.first_name || ''}
                onChange={(e) => set('first_name', e.target.value)}
                data-testid="profile-self-first_name"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('first_name')}`}
              />
            ) : <p className="text-sm text-slate-800 py-2 border-b border-slate-100">{profile.first_name || '—'}</p>}
            <FieldErr name="first_name" errors={errors} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fields.surname')} *</label>
            {editing ? (
              <input
                type="text" value={draft?.surname || ''}
                onChange={(e) => set('surname', e.target.value)}
                data-testid="profile-self-surname"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('surname')}`}
              />
            ) : <p className="text-sm text-slate-800 py-2 border-b border-slate-100">{profile.surname || '—'}</p>}
            <FieldErr name="surname" errors={errors} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email <span className="text-[10px] text-slate-400 ml-1">(read-only — contact support to change)</span>
            </label>
            <p className="text-sm text-slate-700 py-2 border-b border-slate-100">{profile.email}</p>
          </div>
        </div>
      </Section>

      <Section title={t('sections.location_contact', { defaultValue: 'Location & contact' })} icon={MapPin} color="blue">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fields.country')} *</label>
            {editing ? (
              <CountryPicker
                value={draft?.country || ''}
                onChange={(name, code) => {
                  set('country', name);
                  if (!draft?.phone) set('phone_country', code);
                }}
                error={!!errors.country}
                testid="profile-self-country"
              />
            ) : <p className="text-sm text-slate-800 py-2 border-b border-slate-100">{profile.country || '—'}</p>}
            <FieldErr name="country" errors={errors} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Phone *
            </label>
            {editing ? (
              <div data-testid="profile-self-phone" className={`phone-wrap rounded-lg border focus-within:ring-2 ${errors.phone ? 'border-red-400 focus-within:ring-red-400 bg-red-50/30' : 'border-slate-300 focus-within:ring-cyan-500'}`}>
                <PhoneInput
                  international
                  defaultCountry={draft?.phone_country || 'DE'}
                  value={draft?.phone || ''}
                  onChange={(v) => set('phone', v || '')}
                  onCountryChange={(c) => set('phone_country', c || '')}
                  countryCallingCodeEditable={false}
                  placeholder="Enter phone number"
                />
              </div>
            ) : <p className="text-sm text-slate-800 py-2 border-b border-slate-100">{profile.phone || '—'}</p>}
            <FieldErr name="phone" errors={errors} />
          </div>
        </div>
      </Section>

      <Section title={t('sections.evc_registration', { defaultValue: 'EVC.de registration' })} icon={FileText} color="violet">
        {editing ? (
          <>
            <div className="flex gap-2 mb-3">
              <button
                type="button" onClick={() => set('evc_registered', false)}
                data-testid="profile-self-evc-no"
                className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                  !draft?.evc_registered ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >No</button>
              <button
                type="button" onClick={() => set('evc_registered', true)}
                data-testid="profile-self-evc-yes"
                className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                  draft?.evc_registered ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >Yes</button>
            </div>
            {draft?.evc_registered && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">WinOLS / EVC customer ID *</label>
                <input
                  type="text" value={draft?.evc_customer_id || ''}
                  onChange={(e) => set('evc_customer_id', e.target.value)}
                  data-testid="profile-self-evc_customer_id"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('evc_customer_id')}`}
                />
                <FieldErr name="evc_customer_id" errors={errors} />
              </div>
            )}
          </>
        ) : (
          <div>
            <p className="text-sm text-slate-800">{profile.evc_registered ? `Yes — ID: ${profile.evc_customer_id || 'not provided'}` : 'No'}</p>
          </div>
        )}
      </Section>

      <Section title={t('sections.company_invoicing', { defaultValue: 'Company / invoicing details' })} icon={Building2} color="amber">
        <MyInvoicePreviewCard token={token} />
        {editing && (
          <div className="flex gap-2 mb-4">
            <button
              type="button" onClick={() => set('is_company', false)}
              data-testid="profile-self-personal"
              className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                !draft?.is_company ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >{t('sections.personal')}</button>
            <button
              type="button" onClick={() => set('is_company', true)}
              data-testid="profile-self-company"
              className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                draft?.is_company ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >{t('sections.company')}</button>
          </div>
        )}
        {(editing ? draft?.is_company : profile.is_company) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['company_name', 'Company name'],
              ['vat', 'VAT number'],
              ['address', 'Address / PO Box'],
              ['zipcode', 'Zipcode'],
              ['city', 'City'],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label} *</label>
                {editing ? (
                  <input
                    type="text" value={draft?.[key] || ''}
                    onChange={(e) => set(key, e.target.value)}
                    data-testid={`profile-self-${key}`}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls(key)}`}
                  />
                ) : <p className="text-sm text-slate-800 py-2 border-b border-slate-100">{profile[key] || '—'}</p>}
                <FieldErr name={key} errors={errors} />
              </div>
            ))}
          </div>
        ) : (
          !editing && <p className="text-xs text-slate-400 italic">Personal account — no company details.</p>
        )}
      </Section>

      <Section title={t('sections.change_password', { defaultValue: 'Change password' })} icon={Lock} color="amber">
        <p className="text-xs text-slate-500 mb-4 -mt-1">
          {t('password.help', { defaultValue: 'Enter your new password and confirm it. Minimum 8 characters. After saving, use the new password the next time you log in.' })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{t('password.new_password', { defaultValue: 'New password' })} *</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type={pwdShow ? 'text' : 'password'}
                value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                autoComplete="new-password"
                placeholder={t('password.min_chars', { defaultValue: 'At least 8 characters' })}
                data-testid="profile-self-new-password"
                className="w-full pl-9 pr-10 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button
                type="button"
                onClick={() => setPwdShow((s) => !s)}
                className="absolute right-2 top-2 p-1 text-slate-400 hover:text-slate-600"
                data-testid="profile-self-pwd-toggle"
                aria-label={pwdShow ? 'Hide password' : 'Show password'}
              >
                {pwdShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* Strength meter */}
            {pwdNew && (
              <div className="mt-2 flex gap-1" data-testid="profile-self-pwd-strength">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      i < pwdScore
                        ? pwdScore <= 1 ? 'bg-red-400' : pwdScore === 2 ? 'bg-amber-400' : pwdScore === 3 ? 'bg-lime-500' : 'bg-emerald-500'
                        : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>
            )}
            {pwdNew && (
              <p className="text-[11px] mt-1 text-slate-500">
                {pwdScore <= 1 && 'Weak'}
                {pwdScore === 2 && 'Fair — add upper-case, digits or symbols'}
                {pwdScore === 3 && 'Good'}
                {pwdScore === 4 && 'Strong'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{t('password.confirm_password', { defaultValue: 'Confirm new password' })} *</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type={pwdShow ? 'text' : 'password'}
                value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter the new password"
                data-testid="profile-self-confirm-password"
                className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                  pwdConfirm && !pwdMatch
                    ? 'border-red-400 focus:ring-red-400 bg-red-50/30'
                    : pwdMatch ? 'border-emerald-400 focus:ring-emerald-400 bg-emerald-50/30' : 'border-slate-300 focus:ring-amber-500'
                }`}
              />
            </div>
            {pwdConfirm && !pwdMatch && (
              <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid="profile-self-pwd-mismatch">
                <AlertCircle className="w-3 h-3" /> Passwords do not match
              </p>
            )}
            {pwdMatch && (
              <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>
        </div>

        {pwdMsg.text && (
          <div
            className={`mt-3 p-3 rounded-lg text-sm ${
              pwdMsg.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
            data-testid="profile-self-pwd-msg"
          >
            {pwdMsg.type === 'success' && <CheckCircle2 className="w-4 h-4 inline mr-1.5" />}
            {pwdMsg.type === 'error' && <AlertCircle className="w-4 h-4 inline mr-1.5" />}
            {pwdMsg.text}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={changePassword}
            disabled={pwdSaving || !pwdValid}
            data-testid="profile-self-pwd-save"
            className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-md text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {t('sections.change_password', { defaultValue: 'Change password' })}
          </button>
        </div>
      </Section>

      <Section title={t('sections.newsletter', { defaultValue: 'Newsletter' })} icon={Mail} color="emerald">
        <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
          <input
            type="checkbox"
            checked={editing ? !!draft?.subscribed_newsletter : !!profile.subscribed_newsletter}
            disabled={!editing}
            onChange={(e) => set('subscribed_newsletter', e.target.checked)}
            data-testid="profile-self-newsletter"
            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
          />
          <div>
            <p className="text-sm font-semibold text-slate-800">{t('newsletter.subscribe_label', { defaultValue: 'Subscribe to AUTO-REMAP newsletter' })}</p>
            <p className="text-xs text-slate-500">{t('newsletter.subscribe_hint', { defaultValue: 'Receive product news, special offers, and tuning tips. Unsubscribe anytime.' })}</p>
          </div>
        </label>
      </Section>

      <style>{`
        .phone-wrap .PhoneInput { padding: 0.5rem 0.75rem; }
        .phone-wrap .PhoneInputCountrySelect { background: transparent; }
        .phone-wrap input.PhoneInputInput {
          border: 0; outline: none; background: transparent;
          font-size: 0.875rem; padding: 0.25rem 0; flex: 1;
        }
      `}</style>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg bg-${color}-50 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 text-${color}-600`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</p>
      <p className="text-sm font-bold text-slate-800 truncate">{value}</p>
    </div>
  </div>
);


/**
 * MyInvoicePreviewCard — Customer-facing invoice preview.
 *
 * Renders the sample PDF from `GET /api/companies/me/invoice-preview`
 * inline in an <iframe> so the customer can see EXACTLY how their
 * next real invoice will look — with their current billing fields
 * substituted in. If profile fields are missing (no VAT, no address)
 * the preview will visibly show the blanks, prompting the customer to
 * fill them in BEFORE making a payment.
 *
 * Browser quirks: Chrome/Firefox can usually render PDFs inline, but
 * some browsers (Safari iOS, headless Chromium, corporate-locked
 * Edge with no PDF viewer) refuse. We always render a prominent
 * "Open in new tab" button so the customer is never stuck staring at
 * an empty rectangle wondering whether the click did anything.
 */
function MyInvoicePreviewCard({ token }) {
  const { t } = useTranslation('profile');
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const urlRef = useRef('');

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/companies/me/invoice-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      // Some backends mis-tag PDF as application/octet-stream — force
      // the correct MIME so the iframe / new-tab opens it as PDF.
      const pdfBlob = blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(pdfBlob);
      urlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      setErr(e.message || 'Could not load preview');
    } finally { setLoading(false); }
  }, [token]);

  // Clean up the blob URL when the component unmounts.
  useEffect(() => {
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, []);

  return (
    <div className="mb-5 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4" data-testid="my-invoice-preview-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Receipt className="w-5 h-5 text-cyan-700" />
          <div>
            <h4 className="text-sm font-bold text-slate-900">{t('invoice_preview.title', { defaultValue: 'Preview your invoice' })}</h4>
            <p className="text-[11px] text-slate-600">
              {t('invoice_preview.body', { defaultValue: 'See exactly how your next invoice will look with your current billing details. Save your changes first to refresh.' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="my-invoice-preview-open"
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 inline-flex items-center gap-1.5"
            >
              <Eye className="w-3.5 h-3.5" /> {t('invoice_preview.open_pdf', { defaultValue: 'Open PDF' })}
            </a>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            data-testid="my-invoice-preview-refresh"
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
            {previewUrl ? t('invoice_preview.refresh', { defaultValue: 'Refresh preview' }) : t('invoice_preview.generate', { defaultValue: 'Generate preview' })}
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs" data-testid="my-invoice-preview-error">
          {err}
        </div>
      )}

      {previewUrl && (
        <div className="mt-3 relative">
          <iframe
            title="Invoice preview"
            src={previewUrl}
            className="w-full h-[600px] rounded-lg border border-slate-200 bg-white"
            data-testid="my-invoice-preview-pdf"
          />
          <p className="mt-2 text-[11px] text-slate-500 text-center">
            <Trans
              i18nKey="invoice_preview.doesnt_render"
              ns="profile"
              components={{ extlink: <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-700 font-bold underline" /> }}
              defaults="Doesn't render? <extlink>Open the PDF in a new tab</extlink>."
            />
          </p>
        </div>
      )}
    </div>
  );
}
